<script>
import { required, minValue, requiredIf } from "vuelidate/lib/validators";
import Modal from "../../../../components/modals/modal-form.vue";
import api from '../../../../helpers/apirest/api';

import {
    identificationTypeComputed,
    customerComputed,
    sourceDriverComputed,
    targetDriverComputed,
    customerTaskFieldTypeComputed,
} from "@/state/helpers";

const initialState = () => {
    return {
        submitted: false,
        customer_task: {
            customer_id: '',
            source_driver_id: '',
            target_driver_id: '',
            description: '',
            error_emails: null,
            success_emails: null,
            last_sync: null,
            heading_row: null,
            filename: null,
            file_uuid: null,
            sheet_name: null,
            subject: null,
            from: null,

            project_id: null,
            dataset_id: null,
            table_id: null,

            customer_task_fields: []
        }
    };
}

export default {
    emits: ['onSuccess'],
    props: {
        modalId: String,
        id: Number,
        clone:{
            type: Boolean,
            default: false
        }
    },
    data() {
        return initialState();
    },
    components: {
        Modal
    },
    computed: {
        ...identificationTypeComputed,
        ...customerComputed,
        ...sourceDriverComputed,
        ...targetDriverComputed,
        ...customerTaskFieldTypeComputed,
        withFileName(){
            const allowDrivers = ['ftp', 's3', 'gmail'];
            return this.forSourceDriver(allowDrivers);            
        },
        withFileUuid(){
            const allowDrivers = [ 'google_drive'];
            return this.forSourceDriver(allowDrivers);            
        },
        withSubject(){
            const allowDrivers = [ 'gmail'];
            return this.forSourceDriver(allowDrivers);            
        },
        withFrom(){
            const allowDrivers = [ 'gmail'];
            return this.forSourceDriver(allowDrivers);            
        },
        withBigquery(){
            const allowDrivers = [ 'bigquery'];
            return this.forTargetDriver(allowDrivers);            
        },
        specialConditions(){
            return [
                {id: 'overwrite', label: 'Overwrite'},
                //{id: 'exclude', label: 'Exclude'},
            ]
        }
    },
    validations: {
        customer_task: {
            customer_id: { required },
            source_driver_id: { required },
            target_driver_id: { required },
            description: { required },
            success_emails: {  },
            error_emails: {  },
            last_sync: { required },
            heading_row: { required, minValue: minValue(1) },
            filename: { 
                required: requiredIf(function() { 
                    return this.withFileName 
                }), 
            },
            file_uuid: { 
                required: requiredIf(function() { 
                    return this.withFileUuid 
                }), 
            },
            sheet_name: { },
            subject: { },
            from: { },
            project_id: { 
                required: requiredIf(function() { 
                    return this.withBigquery 
                }), 
            },
            dataset_id: { 
                required: requiredIf(function() { 
                    return this.withBigquery 
                }), 
            },
            table_id: { 
                required: requiredIf(function() { 
                    return this.withBigquery 
                }), 
            },
            customer_task_fields: {
                $each: {
                    source_column_name: { required },
                    target_column_name: { required },
                    customer_task_field_type_id: {  },
                    default_value: {  },
                    validation_rules: {  },
                    special_condition: {  },
                }
            }
        }
    },
    methods: {
        forSourceDriver(allowDrivers){
            if(this.sourceDrivers && this.customer_task?.source_driver_id){
                const selectedDriver = this.sourceDrivers.find(item => item.id === this.customer_task?.source_driver_id)
                if(selectedDriver && allowDrivers.includes(selectedDriver?.source_driver_type?.slug)){
                    return true;
                }
            }
            return false;
        },
        forTargetDriver(allowDrivers){
            if(this.targetDrivers && this.customer_task?.target_driver_id){
                const selectedDriver = this.targetDrivers.find(item => item.id === this.customer_task?.target_driver_id)
                if(selectedDriver && allowDrivers.includes(selectedDriver?.target_driver_type?.slug)){
                    return true;
                }
            }
            return false;
        },
        async shown() {

            this.$store.dispatch("identificationType/loadList");
            this.$store.dispatch("customer/loadList");
            this.$store.dispatch("sourceDriver/loadList");
            this.$store.dispatch("targetDriver/loadList");
            this.$store.dispatch("customerTaskFieldType/loadList");

            if (this.id) {

                const response = await api.get({
                    url: `customer-tasks/${this.id}`
                });

                const data = response?.data?.data;

                if(this.clone){
                    console.log(data);
                    data.id = null;
                    data.description = data.description + ' - copy';
                    data?.customer_task_fields?.map(item => {
                        item.id = null;
                        return item;
                    })
                }
                

                this.customer_task = data;
            }
        },
        addRow(){
            this.customer_task.customer_task_fields.push({
                source_column_name:null,
                target_column_name: null,
                customer_task_field_type_id: '',
                default_value: null,
                validation_rules: null,
                special_condition: '',
            });
        },
        deleteRow(key){
            this.customer_task.customer_task_fields.splice(key, 1);
        },
        async confirm() {

            this.submitted = true;
            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            const data = this.customer_task;

            await api.save({
                url: 'customer-tasks',
                id: this.customer_task?.id,
                data: data 
            });

            this.$bvModal.hide(this.modalId);
            this.$emit("onSuccess");
        },
        cancel() {
            this.$bvModal.hide(this.modalId);
        },
        resetModal() {
            Object.assign(this.$data, initialState());
        }
    }
};
</script>

<template>
    <Modal :title="'Customer task form'" :id="modalId" @confirm="confirm" @cancel="cancel" @shown="shown" @hidden="resetModal" :size="'extra-xl'">
        <div class="row">

            <div class="col-lg-4 col-md-12 col-xs-12 mb-2">
                <div class="card h-100">
                    <div class="card-header bg-secondary">
                        <h4 class="card-title text-white">General</h4>
                    </div>
                    <div class="card-body">
                        <div class="row">
                            <div class="form-group col-md-6 col-xs-12">
                                <label for="customer_id">Customer</label>
                                <select v-model="customer_task.customer_id" class="form-control"
                                    :class="{ 'is-invalid': $v.customer_task.customer_id.$error && submitted }">
                                    <option value="">Select</option>
                                    <option v-for="option in customers" v-bind:value="option.id" :key="option.id">
                                        {{ option.fullname }}
                                    </option>
                                </select>
                                <div v-if="!$v.customer_task.customer_id.required && submitted" class="invalid-feedback">Customer is
                                    required</div>
                            </div>
                            <div class="form-group col-md-6 col-xs-12">
                                <label for="description">Name</label>
                                <input id="description" v-model="customer_task.description" type="text" class="form-control" placeholder="Enter name"
                                    :class="{ 'is-invalid': $v.customer_task.description.$error && submitted }" />
                                <div v-if="!$v.customer_task.description.required && submitted" class="invalid-feedback">Name is required</div>
                            </div>
                            <div class="form-group col-md-6 col-xs-12">
                                <label for="success_emails">Success Emails</label>
                                <input id="success_emails" v-model="customer_task.success_emails" type="text" class="form-control" placeholder="Enter emails separated by commas"
                                    :class="{ 'is-invalid': $v.customer_task.success_emails.$error && submitted }" />
                            </div>
                            <div class="form-group col-md-6 col-xs-12">
                                <label for="error_emails">Error Emails</label>
                                <input id="error_emails" v-model="customer_task.error_emails" type="text" class="form-control" placeholder="Enter emails separated by commas"
                                    :class="{ 'is-invalid': $v.customer_task.error_emails.$error && submitted }" />
                            </div>
                            <div class="form-group col-md-6 col-xs-12">
                                <label for="last_sync">Last sync</label>
                                <input id="last_sync" v-model="customer_task.last_sync" type="datetime-local" class="form-control" placeholder="Enter last sync"
                                    :class="{ 'is-invalid': $v.customer_task.last_sync.$error && submitted }" />
                                <div v-if="!$v.customer_task.last_sync.required && submitted" class="invalid-feedback">Last sync is required</div>
                            </div>
                        </div>                        
                    </div>
                </div>
            </div>

            <div class="col-lg-4 col-md-6 col-xs-12 mb-2">
                <div class="card h-100">
                    <div class="card-header bg-secondary">
                        <h4 class="card-title text-white">Source</h4>
                    </div>
                    <div class="card-body">  
                        <div class="row">
                            <div class="form-group col-12">
                                <label for="source_driver_id">Source Driver</label>
                                <select v-model="customer_task.source_driver_id" class="form-control"
                                    :class="{ 'is-invalid': $v.customer_task.source_driver_id.$error && submitted }">
                                    <option value="">Select</option>
                                    <option v-for="option in sourceDrivers" v-bind:value="option.id" :key="option.id">
                                        {{ option.fullname }}
                                    </option>
                                </select>
                                <div v-if="!$v.customer_task.source_driver_id.required && submitted" class="invalid-feedback">Source Driver is
                                    required</div>
                            </div>
                            <div class="form-group col-lg-6 col-md-12">
                                <label for="heading_row">Heading row</label>
                                <input id="heading_row" v-model="customer_task.heading_row" type="number" class="form-control" placeholder="Enter heading row"
                                    :class="{ 'is-invalid': $v.customer_task.heading_row.$error && submitted }" />
                                <div v-if="!$v.customer_task.heading_row.required && submitted" class="invalid-feedback">Heading row is required</div>
                                <div v-else-if="!$v.customer_task.heading_row.minValue && submitted" class="invalid-feedback">Min {{
                    $v.customer_task.heading_row.$params.minValue.min }}</div>
                            </div>
                            <div class="form-group col-lg-6 col-md-12" v-if="withFileUuid">
                                <label for="file_uuid">File uuid</label>
                                <input id="file_uuid" v-model="customer_task.file_uuid" type="text" class="form-control" placeholder="Enter file uuid"
                                    :class="{ 'is-invalid': $v.customer_task.file_uuid.$error && submitted }" />
                                <div v-if="!$v.customer_task.file_uuid.required && submitted" class="invalid-feedback">File uuid is required</div>
                            </div>
                            <div class="form-group col-lg-6 col-md-12" v-if="withFileName">
                                <label for="filename">File name</label>
                                <input id="filename" v-model="customer_task.filename" type="text" class="form-control" placeholder="Enter file name"
                                    :class="{ 'is-invalid': $v.customer_task.filename.$error && submitted }" />
                                <div v-if="!$v.customer_task.filename.required && submitted" class="invalid-feedback">File name is required</div>
                            </div>
                            <div class="form-group col-lg-6 col-md-12">
                                <label for="sheet_name">Sheet name</label>
                                <input id="sheet_name" v-model="customer_task.sheet_name" type="text" class="form-control" placeholder="Enter sheet name"
                                    :class="{ 'is-invalid': $v.customer_task.sheet_name.$error && submitted }" />
                                <div v-if="!$v.customer_task.sheet_name.required && submitted" class="invalid-feedback">Sheet name is required</div>
                            </div>
                            <div class="form-group col-lg-6 col-md-12" v-if="withSubject">
                                <label for="subject">Subject</label>
                                <input id="subject" v-model="customer_task.subject" type="text" class="form-control" placeholder="Enter email subject"
                                    :class="{ 'is-invalid': $v.customer_task.subject.$error && submitted }" />
                                <div v-if="!$v.customer_task.subject.required && submitted" class="invalid-feedback">Subject is required</div>
                            </div>
                            <div class="form-group col-lg-6 col-md-12" v-if="withFrom">
                                <label for="from">From</label>
                                <input id="from" v-model="customer_task.from" type="text" class="form-control" placeholder="Enter email from"
                                    :class="{ 'is-invalid': $v.customer_task.from.$error && submitted }" />
                                <div v-if="!$v.customer_task.from.required && submitted" class="invalid-feedback">From is required</div>
                            </div>
                        </div>      
                    </div>
                </div>
            </div>

            <div class="col-lg-4 col-md-6 col-xs-12 mb-2">
                <div class="card h-100">
                    <div class="card-header bg-secondary">
                        <h4 class="card-title text-white">Target</h4>
                    </div>
                    <div class="card-body">
                        <div class="row">
                            <div class="form-group col-12">
                                <label for="target_driver_id">Target Driver</label>
                                <select v-model="customer_task.target_driver_id" class="form-control"
                                    :class="{ 'is-invalid': $v.customer_task.target_driver_id.$error && submitted }">
                                    <option value="">Select</option>
                                    <option v-for="option in targetDrivers" v-bind:value="option.id" :key="option.id">
                                        {{ option.fullname }}
                                    </option>
                                </select>
                                <div v-if="!$v.customer_task.target_driver_id.required && submitted" class="invalid-feedback">Target Driver is
                                    required</div>
                            </div>
                            <div class="form-group col-lg-6 col-md-12" v-if="withBigquery">
                                <label for="project_id">Project id</label>
                                <input id="project_id" v-model="customer_task.project_id" type="text" class="form-control" placeholder="Enter project id"
                                    :class="{ 'is-invalid': $v.customer_task.project_id.$error && submitted }" />
                                <div v-if="!$v.customer_task.project_id.required && submitted" class="invalid-feedback">Project id is required</div>
                            </div>
                            <div class="form-group col-lg-6 col-md-12" v-if="withBigquery">
                                <label for="dataset_id">Dataset id</label>
                                <input id="dataset_id" v-model="customer_task.dataset_id" type="text" class="form-control" placeholder="Enter dataset id"
                                    :class="{ 'is-invalid': $v.customer_task.dataset_id.$error && submitted }" />
                                <div v-if="!$v.customer_task.dataset_id.required && submitted" class="invalid-feedback">Dataset id is required</div>
                            </div>
                            <div class="form-group col-lg-6 col-md-12" v-if="withBigquery">
                                <label for="table_id">Table id</label>
                                <input id="table_id" v-model="customer_task.table_id" type="text" class="form-control" placeholder="Enter table id"
                                    :class="{ 'is-invalid': $v.customer_task.table_id.$error && submitted }" />
                                <div v-if="!$v.customer_task.table_id.required && submitted" class="invalid-feedback">Table id is required</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </div>

        <div class="row">
            <div class="col-12">   
                
                <div class="card h-100">
                    <div class="card-header card-header-with-action bg-secondary">
                        <h4 class="card-title text-white">Fields</h4>
                        <button type="button" class="btn btn-sm btn-success" v-b-tooltip.hover title="Add field" @click="addRow()"><i class="mdi mdi-plus mr-2"></i></button>
                    </div>
                    <div class="card-body table-responsive">
                        <table class="table table-bordered table-sticky-header">
                            <thead>
                                <tr>
                                    <th scope="col">Source column name</th>
                                    <th scope="col">Target column name</th>
                                    <th scope="col">Type</th>
                                    <th scope="col">Default value</th>
                                    <th scope="col">Validation rules</th>
                                    <th scope="col">Special condition</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(customer_task_field, i) in customer_task?.customer_task_fields" :key="i" :set="v = $v.customer_task.customer_task_fields.$each[i]" class="">
                                    <td>
                                        <div class="form-group">
                                            <input 
                                                type="text"
                                                v-model="customer_task_field.source_column_name"
                                                class="form-control"
                                                :class="{ 'is-invalid': v.source_column_name.$error }"
                                                placeholder="Source column name"
                                            />
                                            <div class="invalid-feedback">
                                                <div>Source column name is required</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div class="form-group">
                                            <input
                                                type="text"
                                                v-model="customer_task_field.target_column_name"
                                                class="form-control"
                                                :class="{ 'is-invalid': v.target_column_name.$error }"
                                                placeholder="Target column name"
                                            />
                                            <div class="invalid-feedback">
                                                <div>Target column name is required</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div class="form-group">
                                            <select v-model="customer_task_field.customer_task_field_type_id" class="form-control"
                                                :class="{ 'is-invalid': v.customer_task_field_type_id.$error && submitted }">
                                                <option value="">Select</option>
                                                <option v-for="option in customerTaskFieldTypes" v-bind:value="option.id" :key="option.id">
                                                    {{ option.name }}
                                                </option>
                                            </select>                                            
                                        </div>
                                    </td>
                                    <td>
                                        <div class="form-group">
                                            <input
                                                type="text"
                                                v-model="customer_task_field.default_value"
                                                class="form-control"
                                                :class="{ 'is-invalid': v.default_value.$error }"
                                                placeholder="Default value"
                                            />
                                        </div>
                                    </td>
                                    <td>
                                        <div class="form-group">
                                            <input
                                                type="text"
                                                v-model="customer_task_field.validation_rules"
                                                class="form-control"
                                                :class="{ 'is-invalid': v.validation_rules.$error }"
                                                placeholder="Validation rules"
                                            />
                                        </div>
                                    </td>
                                    <td>
                                        <div class="form-group">
                                            <select v-model="customer_task_field.special_condition" class="form-control"
                                                :class="{ 'is-invalid': v.special_condition.$error && submitted }">
                                                <option value="">Select</option>
                                                <option v-for="option in specialConditions" v-bind:value="option.id" :key="option.id">
                                                    {{ option.label }}
                                                </option>
                                            </select>
                                        </div>
                                    </td>
                                    <td class="text-center">
                                        <a
                                            href="javascript:void(0);"
                                            class="text-danger"
                                            v-b-tooltip.hover
                                            title="Delete row"
                                            @click="deleteRow(i)"
                                        >
                                            <i class="mdi mdi-trash-can font-size-18"></i>
                                        </a>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </Modal>
</template>